import PictureImage from '../Common/PictureImage';
import { useState, useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import Footer from "../Layouts/Footer";
import "./layout.css";

/* ─────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────── */
const T = {
  blue:       "#1e3a8a",
  blueDark:   "#0f2057",
  blueMid:    "#2548c4",
  blueLight:  "#dce6ff",
  blueXLight: "#f0f4ff",
  orange:     "#f97316",
  orangeDark: "#c45c08",
  orangeLight:"#fff4ed",
  ink:        "#0d1421",
  ink2:       "#1e2a40",
  ink3:       "#4a5568",
  ink4:       "#8896b0",
  white:      "#ffffff",
  bg:         "#f4f7fd",
  border:     "#dde4f5",
};

/** Page typography — simple sans-serif, scoped so we do not override the rest of the app. */
const FONT = "Arial, Helvetica, sans-serif";

/* ─────────────────────────────────────────────
   PAGE SCOPE (injected once)
───────────────────────────────────────────── */
const GLOBAL_CSS = `
  .amazon-fba-shipments-root {
    font-family: ${FONT};
    background: ${T.bg};
    color: ${T.ink};
    -webkit-font-smoothing: antialiased;
  }
`;

function useGlobalStyle(css: string) {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = css;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);
}

/* ─────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────── */
function AnimatedCounter({
  target,
  suffix = "",
  prefix = "",
}: {
  target: number;
  suffix?: string;
  prefix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = 16;
    const increment = target / (duration / step);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, step);
    return () => clearInterval(timer);
  }, [inView, target]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
}

/* ─────────────────────────────────────────────
   COMPLIANCE BADGE (animated checkmark ring)
───────────────────────────────────────────── */
function ComplianceBadge() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true });

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
      <svg width="120" height="120" viewBox="0 0 120 120">
        {/* Background circle */}
        <circle cx="60" cy="60" r="54" fill={T.blueXLight} stroke={T.blueLight} strokeWidth="2" />
        {/* Animated ring */}
        <motion.circle
          cx="60" cy="60" r="50"
          fill="none"
          stroke={T.orange}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="314"
          strokeDashoffset="314"
          animate={inView ? { strokeDashoffset: 0 } : {}}
          transition={{ duration: 1.6, ease: "easeInOut", delay: 0.2 }}
          style={{ transformOrigin: "60px 60px", rotate: "-90deg" }}
        />
        {/* Inner blue ring */}
        <motion.circle
          cx="60" cy="60" r="42"
          fill="none"
          stroke={T.blue}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="264"
          strokeDashoffset="264"
          animate={inView ? { strokeDashoffset: 0 } : {}}
          transition={{ duration: 1.4, ease: "easeInOut", delay: 0.5 }}
          style={{ transformOrigin: "60px 60px", rotate: "-90deg" }}
        />
        {/* Checkmark */}
        <motion.polyline
          points="38,62 53,77 82,46"
          fill="none"
          stroke={T.blue}
          strokeWidth="5.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="60"
          strokeDashoffset="60"
          animate={inView ? { strokeDashoffset: 0 } : {}}
          transition={{ duration: 0.6, ease: "easeOut", delay: 1.4 }}
        />
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PROCESS STEP
───────────────────────────────────────────── */
const STEPS = [
  { icon: "📦", label: "Inventory Received", desc: "Inventory arrives at our Amazon prep centre UK and is logged into our warehouse management system." },
  { icon: "🗂️", label: "Shipment Plan", desc: "Seller Central shipment plans created to meet Amazon compliance requirements exactly." },
  { icon: "🔧", label: "Units Prepped", desc: "Labelling, poly bagging, bubble wrapping, and bundling completed to ASIN requirements." },
  { icon: "📫", label: "Boxed & Labeled", desc: "FBA carton labels, box content labels, and pallet labels applied accurately." },
  { icon: "🚚", label: "Shipped to Amazon FC", desc: "LTL pallets and SPD shipments booked, tracked, and delivered to Amazon fulfilment centres." },
];

type ShipmentService = {
  title: string;
  icon: string;
  short: string;
  body: string;
  highlights: string[];
};

type WhyItem = {
  icon: string;
  title: string;
  body: string;
};

function ProcessFlow() {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        overflowX: "auto",
        paddingBottom: 14,
        paddingTop: 4,
        marginTop: 12,
      }}
    >
      {STEPS.map((step, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 180 }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.18 + 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              background: T.white,
              border: `1.5px solid ${T.border}`,
              borderRadius: 16,
              padding: "20px 18px 24px",
              textAlign: "center",
              boxShadow: "0 2px 14px rgba(30,58,138,.07)",
              cursor: "default",
              transition: "box-shadow .2s, transform .2s",
            }}
            whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(30,58,138,.14)" }}
          >
            {/* Step number (in document flow so horizontal scroll does not clip it) */}
            <div style={{
              display: "inline-block",
              background: i === 0 || i === STEPS.length - 1 ? T.orange : T.blue,
              color: T.white,
              fontFamily: FONT,
              fontSize: 11, fontWeight: 700, letterSpacing: ".1em",
              padding: "4px 14px", borderRadius: 40,
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,.12)",
              marginBottom: 12,
            }}>
              Step {i + 1}
            </div>
            <div style={{ fontSize: 34, marginBottom: 12, lineHeight: 1 }}>{step.icon}</div>
            <div style={{
              fontFamily: FONT, fontSize: 14, fontWeight: 700,
              color: T.blue, marginBottom: 8, lineHeight: 1.3,
            }}>
              {step.label}
            </div>
            <div style={{ fontSize: 12.5, color: T.ink4, lineHeight: 1.65 }}>{step.desc}</div>
          </motion.div>

          {/* Arrow connector */}
          {i < STEPS.length - 1 && (
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={inView ? { opacity: 1, scaleX: 1 } : {}}
              transition={{ delay: i * 0.18 + 0.4, duration: 0.3 }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, flexShrink: 0,
              }}
            >
              <svg width="28" height="16" viewBox="0 0 28 16" fill="none">
                <path d="M0 8 H22" stroke={T.orange} strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M18 2 L26 8 L18 14" stroke={T.orange} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ACCORDION SERVICE CARDS
───────────────────────────────────────────── */
const SERVICES: ShipmentService[] = [
  {
    title: "Shipment Plan Creation in Seller Central",
    icon: "🗂️",
    short: "We build every Amazon shipment plan for you — correctly, first time.",
    body: `Creating shipment plans inside Amazon Seller Central is a time-consuming, error-prone process that can result in split shipments, compliance flags and unexpected FBA fees. Our team creates and manages every shipment plan on your behalf, ensuring the correct workflow — Send to Amazon or legacy Send/Replenish Inventory — is used, units are distributed across the right fulfilment centres and all ASIN-level details are entered precisely. We eliminate the costly mistakes that trigger re-processing fees or inventory holds.`,
    highlights: ["Send to Amazon workflow", "Legacy shipment support", "Split-shipment minimisation", "ASIN-level accuracy"],
  },
  {
    title: "Box Content Labels",
    icon: "🏷️",
    short: "Every carton labelled with Amazon-compliant box content information.",
    body: `Amazon requires box content information for every carton entering an FBA fulfilment centre. Errors here trigger manual processing fees that can run into hundreds of pounds per shipment. We generate and apply all box content labels to your cartons — including the FBA shipment label, FNSKU labels and box content stickers — using your approved dimensions and unit counts. Our double-check process means zero discrepancies between what Seller Central shows and what arrives at the FC.`,
    highlights: ["FBA carton labels", "FNSKU application", "Box content stickers", "Zero discrepancy promise"],
  },
  {
    title: "Pallet Preparation",
    icon: "🔲",
    short: "Amazon-compliant pallet builds for LTL and floor-loaded shipments.",
    body: `Amazon fulfilment centres have exacting pallet requirements: height limits, stretch-wrap spec, floor-load rules and pallet label placement. A non-compliant pallet can be refused at the dock, creating delays, return fees and inventory backlogs. Our team builds every pallet to Amazon's current Partnered Carrier requirements — correctly stacked, corner-boarded, stretch-wrapped and labelled on all four sides. We handle both single-ASIN and mixed-ASIN pallets with equal precision.`,
    highlights: ["Amazon PC pallet spec", "Height compliance", "4-side pallet labelling", "Mixed & single-ASIN"],
  },
  {
    title: "LTL & SPD Shipments",
    icon: "🚛",
    short: "Full pallet LTL and small parcel despatch to Amazon FCs across the UK.",
    body: `We manage both Less-Than-Truckload (LTL) pallet shipments and Small Parcel Delivery (SPD) carton shipments to Amazon fulfilment centres. Our operations team selects the correct shipment type based on your carton count, unit volume and destination FC. For SPD we generate all carrier labels and upload tracking to Seller Central within the required window. For LTL we coordinate the dock booking, PRO number upload and delivery appointment — so your inventory arrives on time and on record.`,
    highlights: ["LTL pallet shipments", "SPD carton despatch", "Tracking upload", "Dock appointment booking"],
  },
  {
    title: "Carrier Booking",
    icon: "📋",
    short: "We book, track and manage every carrier movement on your behalf.",
    body: `Carrier management is one of the most overlooked sources of FBA delays. A missed booking window or incorrect PRO number upload can push your inventory out by days and damage your IPI score. We handle all carrier bookings — Amazon Partnered Carriers for cost efficiency, or your preferred carrier where needed. Every booking is tracked from collection to delivery confirmation, with proactive updates if any issue arises in transit. You get full visibility without managing a single phone call or tracking number yourself.`,
    highlights: ["Amazon Partnered Carrier", "Own-carrier support", "PRO number upload", "Proactive transit updates"],
  },
];

function AccordionCard({ service, index }: { service: ShipmentService; index: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-40px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: T.white,
        border: `1.5px solid ${open ? T.blue : T.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: open ? `0 8px 32px rgba(30,58,138,.12)` : "0 1px 6px rgba(30,58,138,.06)",
        transition: "border-color .2s, box-shadow .2s",
        marginBottom: 12,
      }}
    >
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 14,
          padding: "20px 24px", background: "transparent", border: "none",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: open ? T.blue : T.blueXLight,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, transition: "background .2s",
        }}>
          {service.icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: FONT, fontSize: 15, fontWeight: 700,
            color: open ? T.blue : T.ink, marginBottom: 3, transition: "color .2s",
          }}>
            {service.title}
          </div>
          <div style={{ fontSize: 13, color: T.ink4, lineHeight: 1.5 }}>{service.short}</div>
        </div>
        <motion.div
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.22 }}
          style={{
            width: 28, height: 28, borderRadius: "50%",
            border: `1.5px solid ${open ? T.orange : T.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: open ? T.orange : T.ink4, fontSize: 18, flexShrink: 0,
            background: open ? T.orangeLight : "transparent",
            transition: "border-color .2s, background .2s",
          }}
        >
          +
        </motion.div>
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              padding: "0 24px 24px 24px",
              borderTop: `1px solid ${T.border}`,
              paddingTop: 20,
            }}>
              <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.85, marginBottom: 18 }}>
                {service.body}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {service.highlights.map((h: string) => (
                  <span key={h} style={{
                    fontSize: 12, fontWeight: 600, color: T.blue,
                    background: T.blueLight, padding: "4px 12px",
                    borderRadius: 40, border: `1px solid rgba(30,58,138,.18)`,
                  }}>{h}</span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   STATS ROW
───────────────────────────────────────────── */
const STATS = [
  { num: 500, suffix: "+", label: "Shipments Created", sub: "and growing every quarter" },
  { num: 99.9, suffix: "%", label: "On-Time Delivery", sub: "to Amazon fulfilment centres" },
  { num: 0, suffix: "", label: "Suspension History", sub: "zero compliance failures, ever" },
  { num: 100, suffix: "%", label: "Compliance Rate", sub: "on all Amazon shipment plans" },
];

/* ─────────────────────────────────────────────
   WHY CHOOSE US — feature grid
───────────────────────────────────────────── */
const WHY = [
  { icon: "🎯", title: "Amazon-Specialist Team", body: "Amazon-trained specialists manage every shipment using proven FBA workflows, Seller Central expertise, and deep knowledge of Amazon compliance requirements." },
  { icon: "⚡", title: "Fast Turnaround", body: "Amazon FBA prep services UK completed within 48–72 hours of receipt. Peak-season volumes are managed proactively to protect delivery schedules and maintain uninterrupted inventory flow." },
  { icon: "🔍", title: "Double-Check Process", body: "Every shipment plan undergoes independent verification. Box quantities, unit counts, and Amazon FNSKU labelling assignments are checked against purchase orders." },
  { icon: "📊", title: "Seller Central Access", body: "We operate within your Seller Central account using restricted access permissions, giving you complete visibility and control while our team manages daily operations." },
  { icon: "🌐", title: "Multi-Marketplace", body: "We support Amazon UK, EU, and US marketplaces. Whether shipping to Amazon.co.uk, Amazon.de, or Amazon.com, our team understands marketplace-specific compliance requirements and shipment planning processes." },
  { icon: "💬", title: "Dedicated Account Manager", body: "A dedicated account manager provides direct support via WhatsApp, email, or Slack. Receive proactive shipment updates and clear communication without chasing progress." },
];

/* ─────────────────────────────────────────────
   SECTION WRAPPER
───────────────────────────────────────────── */
function Section({
  children,
  bg = T.bg,
  style = {},
}: {
  children: ReactNode;
  bg?: string;
  style?: CSSProperties;
}) {
  return (
    <section style={{
      background: bg, padding: "80px 24px",
      ...style,
    }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      fontFamily: FONT, fontSize: 11, fontWeight: 700,
      letterSpacing: ".14em", textTransform: "uppercase",
      color: T.orange, marginBottom: 12,
    }}>
      <span style={{ display: "block", width: 24, height: 2, background: T.orange, borderRadius: 2 }} />
      {children}
    </div>
  );
}

function SectionTitle({ children, center = false }: { children: ReactNode; center?: boolean }) {
  return (
    <h2 style={{
      fontSize: "clamp(1.6rem, 3vw, 2.4rem)", fontWeight: 800,
      color: T.ink, letterSpacing: "-.025em", lineHeight: 1.1,
      marginBottom: 14, textAlign: center ? "center" : "left",
    }}>
      {children}
    </h2>
  );
}

function SectionDesc({ children, center = false }: { children: ReactNode; center?: boolean }) {
  return (
    <p style={{
      fontSize: 16, color: T.ink3, lineHeight: 1.8, fontWeight: 300,
      maxWidth: 640, marginBottom: 48,
      margin: center ? "0 auto 48px" : "0 0 48px",
      textAlign: center ? "center" : "left",
    }}>
      {children}
    </p>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE COMPONENT
───────────────────────────────────────────── */
export default function AmazonFBAShipments() {
  useGlobalStyle(GLOBAL_CSS);

  return (
    <>
      <div className="amazon-fba-shipments-root">
      {/* Site header is provided by App.tsx; no duplicate nav here. */}

      {/* ── HERO ── */}
      <HeroSection />

      {/* ── PROCESS FLOW ── */}
      <Section bg={T.white} style={{ paddingTop: 112 }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <Eyebrow>How it works</Eyebrow>
          <SectionTitle center>Your Amazon FBA Shipment Managed End to End</SectionTitle>
          <SectionDesc center>
          From arrival at our Amazon prep centre in the UK to final delivery at an Amazon fulfilment centre, every stage is tracked, managed, and verified by our specialist team. Our Amazon FBA prep services UK eliminate compliance risks, prevent shipment delays, and remove the complexity of coordinating carriers, documentation, and inventory movements yourself.
          </SectionDesc>
        </div>
        <ProcessFlow />
      </Section>

      {/* ── SERVICES ACCORDION ── */}
      <Section bg={T.bg}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}
          className="services-grid">
          <div>
            <Eyebrow>Our services</Eyebrow>
            <SectionTitle>Everything your Amazon FBA shipments need under one roof</SectionTitle>
            <SectionDesc>
            Our Amazon FBA shipment management service handles every stage, from Seller Central shipment planning and Amazon FBA prep services UK to carrier booking and fulfilment centre delivery. Our trained team manages compliance, documentation, and scheduling requirements, helping UK sellers avoid costly errors, missed booking slots, shipment delays, and unnecessary operational complexity.
            </SectionDesc>
            <div style={{
              background: T.blue, borderRadius: 14, padding: "24px 28px",
              color: T.white,
            }}>
              <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
                Why sellers trust Pick Pack Pro
              </div>
              {[
                "Amazon-trained FBA specialists, not general warehouse operatives",
                "Seller Central access remains under your control at all times",
                "Every shipment plan verified against Amazon compliance requirements",
                "Direct communication via WhatsApp, email, or Slack for rapid support",
              ].map((point) => (
                <div key={point} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
                  <span style={{ color: T.orange, fontWeight: 700, marginTop: 2, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,.8)", lineHeight: 1.6 }}>{point}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            {SERVICES.map((s, i) => (
              <AccordionCard key={s.title} service={s} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* ── STATS ROW ── */}
      <StatsSection />

      {/* ── WHY CHOOSE US ── */}
      <Section bg={T.white}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <Eyebrow>Why Pick Pack Pro</Eyebrow>
          <SectionTitle center>The Amazon FBA prep centre built for serious sellers</SectionTitle>
          <SectionDesc center>
          Unlike generalist 3PL providers, our Amazon prep centre in the UK is built exclusively for Amazon FBA prep services. Every process, workflow, and quality check is designed to meet Amazon compliance requirements, accelerate turnaround times, and support seller growth. Here is how that specialist's focus benefits your business every day.</SectionDesc>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 20,
        }}>
          {WHY.map((item, i) => (
            <WhyCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </Section>

      {/* ── COMPLIANCE BADGE ── */}
      <ComplianceSection />

      {/* ── BOTTOM CTA ── */}
      <CTASection />

      <Footer />
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
const HERO_BG_URL =
  "/amazonfba/Amazonfbashipment-image.webp";

function HeroSection() {
  return (
    <section className="hero-section fba-page-hero">
      <PictureImage
        className="fba-page-hero__bg"
        src={HERO_BG_URL}
        alt="Amazon FBA shipments UK logistics and warehouse operations"
        width={1600}
        height={900}
        decoding="async"
        isHero
      />
      <div className="fba-page-hero__overlay" aria-hidden />
      {/* Geometric decoration */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        style={{
          position: "absolute", right: -120, top: -120, zIndex: 2,
          width: 560, height: 560, borderRadius: "50%",
          border: `90px solid rgba(249,115,22,.09)`,
        }}
      />
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: "easeOut", delay: 0.15 }}
        style={{
          position: "absolute", right: 80, top: 80, zIndex: 2,
          width: 280, height: 280, borderRadius: "50%",
          border: `50px solid rgba(249,115,22,.06)`,
        }}
      />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute", left: -80, bottom: -80, zIndex: 2,
          width: 360, height: 360, borderRadius: "50%",
          border: `2px dashed rgba(255,255,255,.07)`,
        }}
      />
      {/* Grid pattern */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 2,
        backgroundImage: `radial-gradient(rgba(255,255,255,.04) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }} />

      <div className="fba-page-hero__content">
        <div style={{ maxWidth: 760, textAlign: "left" }}>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "rgba(249,115,22,.15)", border: "1px solid rgba(249,115,22,.35)",
              borderRadius: 40, padding: "6px 16px", marginBottom: 28,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.orange, display: "block" }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: T.white }}>
              Amazon FBA shipment specialists UK
            </span>
          </motion.div>

          {/* H1 */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontWeight: 800, color: T.white,
              lineHeight: 1.06, letterSpacing: "-.03em", marginBottom: 24,
            }}
          >
            
            End-to-End  <span style={{ color: T.orange }}>Amazon FBA Shipment </span> Management

           
           
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            style={{
              color: "rgba(255,255,255,.72)", lineHeight: 1.8,
              maxWidth: 600, marginBottom: 40, fontWeight: 300,
            }}
          >
            We manage your entire Amazon FBA shipment process, from Seller Central shipment creation and Amazon FBA prep services UK to pallet building and carrier booking. Every shipment complies with Amazon's compliance requirements and is delivered to the correct fulfilment centre on schedule. No compliance failures, no account suspensions, and complete visibility throughout the process.
          </motion.p>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.32 }}
            style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 48 }}
          >
            <motion.button
              whileHover={{ scale: 1.04, background: T.orangeDark }}
              whileTap={{ scale: 0.97 }}
              style={{
                background: T.orange, color: T.white,
                fontFamily: FONT, fontSize: 15, fontWeight: 700,
                padding: "16px 36px", borderRadius: 40, border: "none", cursor: "pointer",
                letterSpacing: ".03em", boxShadow: "0 8px 28px rgba(249,115,22,.35)",
              }}
            >
              Let Us Handle Your Shipments →
            </motion.button>
            <motion.button
              whileHover={{ background: "rgba(255,255,255,.12)" }}
              style={{
                background: "transparent", color: "rgba(255,255,255,.85)",
                fontFamily: FONT, fontSize: 15, fontWeight: 600,
                padding: "15px 30px", borderRadius: 40,
                border: "1.5px solid rgba(255,255,255,.25)", cursor: "pointer",
              }}
            >
              View our pricing
            </motion.button>
          </motion.div>

          {/* Trust pills */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.46 }}
            style={{ display: "flex", flexWrap: "wrap", gap: 10 }}
          >
            {["Zero suspension history", "Amazon-trained team", "UK fulfilment centre", "LTL & SPD shipments"].map(t => (
              <span key={t} style={{
                fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,.65)",
                background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)",
                padding: "5px 14px", borderRadius: 40, display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{ color: T.orange, fontWeight: 700 }}>✓</span>{t}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   STATS SECTION
───────────────────────────────────────────── */
function StatsSection() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  return (
    <section ref={ref} style={{
      background: T.blue, padding: "72px 24px",
    }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase",
            color: T.orange, marginBottom: 10,
          }}>Track record</div>
          <h2 style={{
            fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 800,
            color: T.white, letterSpacing: "-.02em",
          }}>
            Numbers that prove our Amazon FBA expertise
          </h2>
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 2,
        }}>
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{
                textAlign: "center", padding: "40px 24px",
                background: i % 2 === 0 ? "rgba(255,255,255,.04)" : "transparent",
                borderRadius: 12,
              }}
            >
              <div style={{
                fontSize: "clamp(2.4rem, 5vw, 3.6rem)", fontWeight: 800,
                color: T.orange, lineHeight: 1, marginBottom: 10,
              }}>
                <AnimatedCounter
                  target={typeof stat.num === "number" ? stat.num : parseFloat(stat.num)}
                  suffix={stat.suffix}
                />
              </div>
              <div style={{
                fontSize: 16, fontWeight: 700, color: T.white, marginBottom: 6,
              }}>{stat.label}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.5 }}>{stat.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   WHY CARD
───────────────────────────────────────────── */
function WhyCard({ item, index }: { item: WhyItem; index: number }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-40px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: T.white, border: `1.5px solid ${T.border}`,
        borderRadius: 14, padding: "28px 24px",
        boxShadow: "0 1px 6px rgba(30,58,138,.06)",
        transition: "border-color .2s, box-shadow .2s, transform .2s",
      }}
      whileHover={{
        y: -4, borderColor: T.blue,
        boxShadow: "0 10px 32px rgba(30,58,138,.12)",
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 12, background: T.blueXLight,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, marginBottom: 16,
      }}>{item.icon}</div>
      <h3 style={{
        fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 10,
      }}>{item.title}</h3>
      <p style={{ fontSize: 13.5, color: T.ink3, lineHeight: 1.75 }}>{item.body}</p>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   COMPLIANCE SECTION
───────────────────────────────────────────── */
function ComplianceSection() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  const points = [
    { label: "Amazon Compliance Assured", desc: "Every Amazon FBA prep service follows current Seller Central policies and Amazon compliance requirements." },
    { label: "Zero Suspension History", desc: "No client account has ever been suspended due to errors within our Amazon FBA prep processes." },
    { label: "Continuous Compliance Monitoring", desc: "We monitor Amazon policy updates regularly to ensure shipments always meet the latest requirements." },
    { label: "Complete Seller Central Audit Trail", desc: "Every action is recorded, creating a transparent audit trail across shipment planning and account activity." },
  ];

  return (
    <section ref={ref} style={{
      background: T.blueDark, padding: "88px 24px 96px", position: "relative", overflow: "hidden",
    }}>
      {/* BG deco */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `radial-gradient(rgba(255,255,255,.03) 1px, transparent 1px)`,
        backgroundSize: "36px 36px",
      }}/>
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        style={{
          position: "absolute", right: -100, top: -100,
          width: 400, height: 400, borderRadius: "50%",
          border: "2px dashed rgba(249,115,22,.12)",
        }}
      />
      <div style={{ maxWidth: 1120, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center",
        }}>
          {/* Left */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase",
              color: T.orange, marginBottom: 14, display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ width: 24, height: 2, background: T.orange, display: "block", borderRadius: 2 }}/>
              Amazon compliance guaranteed
            </div>
            <h2 style={{
              fontSize: "clamp(1.6rem, 3vw, 2.3rem)", fontWeight: 800, color: T.white,
              letterSpacing: "-.025em", lineHeight: 1.1, marginBottom: 20,
            }}>
              Every shipment built to Amazon's exact FBA standards
            </h2>
            <p style={{
              fontSize: 15, color: "rgba(255,255,255,.6)", lineHeight: 1.85, fontWeight: 300, marginBottom: 36,
            }}>
              Amazon compliance errors can lead to additional fees, inventory delays, shipment rejections, and account performance issues. Our Amazon FBA prep services in the UK are designed to eliminate these risks through rigorous quality controls, verified workflows, and compliance checks applied to every shipment before dispatch to Amazon fulfilment centres.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {points.map((p, i) => (
                <motion.div
                  key={p.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: i * 0.1 + 0.3, duration: 0.5 }}
                  style={{ display: "flex", gap: 14, alignItems: "flex-start" }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    background: "rgba(249,115,22,.2)", border: "1.5px solid rgba(249,115,22,.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.orange, fontWeight: 700, fontSize: 13, marginTop: 2,
                  }}>✓</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.white, marginBottom: 4 }}>
                      {p.label}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(255,255,255,.5)", lineHeight: 1.6 }}>{p.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right — animated badge */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 32 }}>
            <ComplianceBadge />
            <div style={{
              fontSize: 18, fontWeight: 800, color: T.white, textAlign: "center", letterSpacing: "-.01em",
            }}>Amazon Compliance Guaranteed</div>
            <div style={{
              background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)",
              borderRadius: 14, padding: "28px 32px", width: "100%", maxWidth: 340,
            }}>
              {[
                ["Shipments handled", "500+"],
                ["Compliance failure rate", "0.00%"],
                ["Avg. FC check-in time", "< 48h"],
                ["Client retention rate", "96%"],
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,.07)",
                }}>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,.5)" }}>{k}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: T.orange }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   BOTTOM CTA SECTION
───────────────────────────────────────────── */
function CTASection() {
  const ref = useRef(null);
  const inView = useInViewOnce(ref, { once: true, margin: "-60px" });

  return (
    <section ref={ref} style={{ padding: "64px 24px 80px", background: T.bg }}>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        style={{
          maxWidth: 1120, margin: "0 auto",
          background: `linear-gradient(135deg, ${T.blue} 0%, ${T.blueDark} 100%)`,
          borderRadius: 20, padding: "72px 56px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 32, flexWrap: "wrap",
          position: "relative", overflow: "hidden",
        }}
      >
        {/* Deco circles */}
        <div style={{
          position: "absolute", right: -80, top: -80,
          width: 340, height: 340, borderRadius: "50%",
          border: `80px solid rgba(249,115,22,.09)`,
          pointerEvents: "none",
        }}/>
        <div style={{
          position: "absolute", left: -40, bottom: -40,
          width: 200, height: 200, borderRadius: "50%",
          border: `50px solid rgba(255,255,255,.04)`,
          pointerEvents: "none",
        }}/>

        <div style={{ position: "relative", zIndex: 1, maxWidth: 560 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase",
            color: T.orange, marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ width: 20, height: 2, background: T.orange, display: "block" }}/>
            Start today
          </div>
          <h2 style={{
            fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)", fontWeight: 800, color: T.white,
            letterSpacing: "-.025em", lineHeight: 1.08, marginBottom: 16,
          }}>
            Let Us Handle Your<br />
            <span style={{ color: T.orange }}>FBA Shipments</span>
          </h2>
          <p style={{
            fontSize: 15, color: "rgba(255,255,255,.6)", lineHeight: 1.8, fontWeight: 300,
          }}>
            Share your prep volumes, product categories, and target marketplaces. We will provide a transparent quote based on our Amazon FBA prep services UK rate card, with no hidden fees or long-term commitments. Your inventory could be moving through our Amazon prep centre in the UK within days, with support from a dedicated account manager.
          </p>
        </div>

        <div style={{
          position: "relative", zIndex: 1,
          display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-end",
        }}>
          <motion.button
            whileHover={{ scale: 1.04, background: T.orangeDark, boxShadow: "0 14px 40px rgba(249,115,22,.45)" }}
            whileTap={{ scale: 0.97 }}
            style={{
              background: T.orange, color: T.white,
              fontFamily: FONT, fontSize: 16, fontWeight: 700,
              padding: "18px 44px", borderRadius: 50,
              border: "none", cursor: "pointer",
              letterSpacing: ".03em",
              boxShadow: "0 8px 28px rgba(249,115,22,.35)",
              whiteSpace: "nowrap",
            }}
          >
            Let Us Handle Your FBA Shipments →
          </motion.button>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.38)", textAlign: "center" }}>
            No commitment · Reply within 1 business day
          </div>
        </div>
      </motion.div>
    </section>
  );
}
